import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { expandSteps, readSchedule, type ExpandStep } from './child-ticket-expand.js';

/**
 * ダイアログのボタン。表の見出し脇と同じ寸法（`.child-sheet-button`）を使い、
 * 画面の中でボタンの大きさが 2 種類に割れないようにする。
 * @graph-connects none
 */
function DialogButton({
  tone,
  onClick,
  children,
}: {
  /** 色。`chunk` は進める側、`neutral` は戻る / 断る側。 */
  tone: 'chunk' | 'neutral';
  /** 押したときの処理。 */
  onClick: () => void;
  /** ボタンの文言。 */
  children: ReactNode;
}): ReactNode {
  return (
    <button type="button" data-tone={tone} className="child-sheet-button" onClick={onClick}>
      {children}
    </button>
  );
}

/** 展開まで通し終えたときに渡す内容。 */
export interface ExpandConfirmed {
  /** 基本設計開始予定日（ISO 形式）。 */
  startDate: string;
  /** 基本設計終了予定日（ISO 形式）。 */
  endDate: string;
  /** 開発リーダーを押した人へ付け替えるか。 */
  takeOverDevLeader: boolean;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計展開の前に通す確認ダイアログ。pt 超過の警告 → 開発リーダーの引き受け確認 → 基本設計の開始/終了予定日の入力の順に 1 段ずつ聞き、途中で断られたら展開を取り消す
 * @graph-connects expandSteps [calls] 通すべき確認の段
 * @graph-connects readSchedule [calls] 開始/終了予定日の受け取り判定
 */
export function ChildTicketExpandDialog({
  open,
  totalPoint,
  overPointLimit,
  isDevLeader,
  devLeaderName,
  onClose,
  onCancelled,
  onConfirmed,
}: {
  /** 開いているか。 */
  open: boolean;
  /** 展開しようとしている合計 pt。 */
  totalPoint: number;
  /** 合計 pt が上限を超えているか。 */
  overPointLimit: boolean;
  /** 押した人が母票の開発リーダーか。 */
  isDevLeader: boolean;
  /** 母票の開発リーダー名（未設定なら null）。 */
  devLeaderName: string | null;
  /** 閉じる（何もせず戻る）。 */
  onClose: () => void;
  /** 展開を取り消した（開発リーダーの引き受けを断った）。 */
  onCancelled: () => void;
  /** 最後まで通した。 */
  onConfirmed: (input: ExpandConfirmed) => void;
}): ReactNode {
  const { t } = useTranslation();
  const steps = expandSteps({ overPointLimit, isDevLeader });
  const [index, setIndex] = useState(0);
  const [takeOver, setTakeOver] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [error, setError] = useState<'missing' | 'order' | null>(null);

  const step: ExpandStep | undefined = steps[index];

  const reset = (): void => {
    setIndex(0);
    setTakeOver(false);
    setStart('');
    setEnd('');
    setError(null);
  };
  const close = (): void => {
    reset();
    onClose();
  };
  const next = (): void => {
    setIndex((current) => current + 1);
  };

  if (step === undefined) return null;

  const body = ((): { title: string; content: ReactNode; footer: ReactNode } => {
    if (step === 'pointWarning') {
      return {
        title: t('childTicketPlan.expand.pointWarning.title'),
        content: (
          <p className="text-sm leading-7 text-[var(--color-text-secondary)]">
            {t('childTicketPlan.expand.pointWarning.body', { total: totalPoint })}
          </p>
        ),
        footer: (
          <>
            <DialogButton tone="neutral" onClick={close}>
              {t('childTicketPlan.expand.back')}
            </DialogButton>
            <DialogButton tone="chunk" onClick={next}>
              {t('childTicketPlan.expand.pointWarning.proceed')}
            </DialogButton>
          </>
        ),
      };
    }
    if (step === 'leaderSwap') {
      return {
        title: t('childTicketPlan.expand.leaderSwap.title'),
        content: (
          <p className="text-sm leading-7 text-[var(--color-text-secondary)]">
            {t('childTicketPlan.expand.leaderSwap.body', {
              leader: devLeaderName ?? t('childTicketPlan.expand.leaderSwap.unset'),
            })}
          </p>
        ),
        footer: (
          <>
            <DialogButton
              tone="neutral"
              onClick={() => {
                // 引き受けないなら展開そのものを取り消す（開発リーダー以外は展開できない運用）
                reset();
                onCancelled();
              }}
            >
              {t('childTicketPlan.expand.leaderSwap.decline')}
            </DialogButton>
            <DialogButton
              tone="chunk"
              onClick={() => {
                setTakeOver(true);
                next();
              }}
            >
              {t('childTicketPlan.expand.leaderSwap.accept')}
            </DialogButton>
          </>
        ),
      };
    }
    return {
      title: t('childTicketPlan.expand.schedule.title'),
      content: (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {(
              [
                ['start', start, setStart],
                ['end', end, setEnd],
              ] as const
            ).map(([key, value, setValue]) => (
              <label key={key} className="block space-y-1.5">
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {t(`childTicketPlan.expand.schedule.${key}`)}
                </span>
                <input
                  type="date"
                  value={value}
                  // 終了は開始より前を選べないようにする（選ばせてから弾くより早い）
                  min={key === 'end' && start !== '' ? start : undefined}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setError(null);
                  }}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-border-focus)] focus:outline-none"
                />
              </label>
            ))}
          </div>
          {error === null ? null : (
            <p className="text-xs leading-6" style={{ color: 'var(--color-error)' }}>
              {t(`childTicketPlan.expand.schedule.${error}Error`)}
            </p>
          )}
        </div>
      ),
      footer: (
        <>
          <DialogButton tone="neutral" onClick={close}>
            {t('childTicketPlan.expand.back')}
          </DialogButton>
          <DialogButton
            tone="chunk"
            onClick={() => {
              const result = readSchedule(start, end);
              if (!result.ok) {
                setError(result.reason);
                return;
              }
              const confirmed = {
                startDate: result.startDate,
                endDate: result.endDate,
                takeOverDevLeader: takeOver,
              };
              reset();
              onConfirmed(confirmed);
            }}
          >
            {t('childTicketPlan.expand.schedule.submit')}
          </DialogButton>
        </>
      ),
    };
  })();

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={body.title}
      // 段を進めるたびに中身が入れ替わるので、弾む動きをダイアログ側で受ける
      className="child-expand-dialog"
      footer={<div className="flex justify-end gap-2">{body.footer}</div>}
    >
      {body.content}
    </Modal>
  );
}
